import { ASPEN_PRICING } from '../config/pricing';
import React, { useEffect, useState } from 'react';
import { authApi } from '../services/api';
import Turnstile from './Turnstile';
import { referralFromUrl, saveReferralNotice } from '../services/referrals';

interface LoginProps {
  onLoginSuccess: (token: string, user: any) => void;
}

const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const phoneNumber = value.replace(/\D/g, '');

  // Handle different lengths
  if (phoneNumber.length === 0) return '';
  if (phoneNumber.length <= 3) return phoneNumber;
  if (phoneNumber.length <= 6) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  if (phoneNumber.length <= 10) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;

  // For numbers with country code
  if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
    return `+1 ${phoneNumber.slice(1, 4)}-${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7)}`;
  }

  return `+${phoneNumber.slice(0, -10)} ${phoneNumber.slice(-10, -7)}-${phoneNumber.slice(-7, -4)}-${phoneNumber.slice(-4)}`;
};

const normalizePhoneNumber = (formatted: string): string => {
  const digits = formatted.replace(/\D/g, '');

  // Handle test number
  if (digits === '8675309') return '8675309';

  // Add +1 for US numbers
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

  return `+${digits}`;
};

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [formattedPhone, setFormattedPhone] = useState('');
  const [code, setCode] = useState('');
  const [referralCode, setReferralCode] = useState(referralFromUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeExpiry, setCodeExpiry] = useState<Date | null>(null);
  const [now, setNow] = useState(Date.now());
  const [config, setConfig] = useState<{ turnstile_required: boolean; turnstile_site_key: string | null; resend_after_seconds: number } | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [challengeKey, setChallengeKey] = useState(0);
  const [retryByPhone, setRetryByPhone] = useState<Record<string, number>>(() => {
    try { return JSON.parse(sessionStorage.getItem('aspen_verification_retry') || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    let active = true;
    authApi.getConfig().then(value => { if (active) setConfig(value); }).catch(() => {
      if (active) setError('Verification is temporarily unavailable. Please refresh and try again.');
    });
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);
  const retrySeconds = Math.max(0, Math.ceil(((retryByPhone[phoneNumber] || 0) - now) / 1000));
  const retryUnit = retrySeconds >= 3600 ? 'hour' : retrySeconds >= 60 ? 'minute' : 'second';
  const retryCount = Math.ceil(retrySeconds / (retrySeconds >= 3600 ? 3600 : retrySeconds >= 60 ? 60 : 1));
  const retryLabel = `${retryCount} ${retryUnit}${retryCount === 1 ? '' : 's'}`;
  const challengeReady = Boolean(config && (!config.turnstile_required || turnstileToken));
  const rememberRetry = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    const next = { ...retryByPhone, [phoneNumber]: Date.now() + seconds * 1000 };
    setRetryByPhone(next);
    setNow(Date.now());
    try { sessionStorage.setItem('aspen_verification_retry', JSON.stringify(next)); } catch { /* Backend still enforces limits. */ }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setFormattedPhone(formatted);
    setPhoneNumber(normalizePhoneNumber(formatted));
  };

  const handleSendCode = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (loading || retrySeconds > 0 || !challengeReady) return;
    setLoading(true);
    setError('');

    try {
      const response = await authApi.sendCode(phoneNumber, turnstileToken || undefined);
      setStep('code');
      setCode('');
      setCodeExpiry(new Date(Date.now() + response.expires_in_minutes * 60 * 1000));
      rememberRetry(response.retry_after_seconds || config?.resend_after_seconds || 60);
    } catch (err: any) {
      rememberRetry(Number(err.response?.headers?.['retry-after']));
      const detail = err.response?.data?.detail;
      const isValidationError = err.response?.status === 422;
      const defaultValidationMessage = 'Please enter a valid US phone number including the area code.';

      let message: string | undefined;

      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        const firstDetail = detail[0];
        if (typeof firstDetail === 'string') {
          message = firstDetail;
        } else if (firstDetail) {
          if (typeof firstDetail.detail === 'string') {
            message = firstDetail.detail;
          } else if (typeof firstDetail.msg === 'string') {
            message = firstDetail.msg;
          }

          if (
            firstDetail.type === 'value_error.str.regex' ||
            message?.toLowerCase().includes('match pattern')
          ) {
            message = defaultValidationMessage;
          }
        }
      } else if (detail && typeof detail === 'object') {
        if (typeof detail.detail === 'string') {
          message = detail.detail;
        } else if (typeof detail.msg === 'string') {
          message = detail.msg;
        }
      }

      if (isValidationError) {
        setError(message || defaultValidationMessage);
      } else {
        setError(message || 'Failed to send verification code');
      }
    } finally {
      setLoading(false);
      setTurnstileToken(null);
      setChallengeKey(value => value + 1);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authApi.verifyCode(phoneNumber, code, referralCode.trim() || undefined);
      if (response.referral?.message) saveReferralNotice(response.referral.message);
      localStorage.setItem('auth_token', response.access_token);
      onLoginSuccess(response.access_token, response.user);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setCode('');
    setError('');
    setCodeExpiry(null);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  const timeRemaining = codeExpiry ? Math.max(0, Math.floor((codeExpiry.getTime() - now) / 1000)) : 0;
  const minutesRemaining = Math.floor(timeRemaining / 60);
  const secondsRemaining = timeRemaining % 60;

  return (
    <div className="min-h-screen flex items-center justify-center bg-aspen-warm-ivory dark:bg-aspen-night-sage px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-aspen-deep-eucalyptus dark:text-aspen-pearl">
            Welcome to Ask Aspen
          </h2>
          <p className="mt-2 text-sm text-aspen-sage-teal dark:text-aspen-gentle-teal">
            {step === 'phone' ? 'Enter your phone number to get started' : 'Enter the verification code'}
          </p>
        </div>

        {config?.turnstile_required && config.turnstile_site_key && (
          <Turnstile siteKey={config.turnstile_site_key} resetKey={challengeKey} onToken={setTurnstileToken} onError={setError} />
        )}
        {config?.turnstile_required && !config.turnstile_site_key && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">Verification is temporarily unavailable. Please try again later.</p>
        )}

        {step === 'phone' ? (
          <form className="mt-8 space-y-6" onSubmit={handleSendCode}>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-aspen-deep-eucalyptus dark:text-aspen-pearl mb-2">
                Phone Number
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                disabled={loading}
                required
                value={formattedPhone}
                onChange={handlePhoneChange}
                placeholder="123-456-7890"
                className="appearance-none relative block w-full px-3 py-3 border border-aspen-blush-clay/30 dark:border-aspen-moonlit-clay placeholder-aspen-sage-teal/50 text-aspen-deep-eucalyptus dark:text-aspen-pearl bg-white dark:bg-aspen-twilight-teal rounded-lg focus:outline-none focus:ring-2 focus:ring-aspen-sage-teal focus:border-aspen-sage-teal focus:z-10 sm:text-sm"
              />
              <p className="mt-1 text-xs text-aspen-sage-teal/70 dark:text-aspen-gentle-teal/70">
                We'll send you a verification code via SMS
              </p>
            </div>

            <div>
              <label htmlFor="referral-code" className="block text-sm font-medium text-aspen-deep-eucalyptus dark:text-aspen-pearl mb-2">Referral code (optional)</label>
              <input id="referral-code" name="referral_code" value={referralCode} onChange={event => setReferralCode(event.target.value.trim())} maxLength={32} pattern="[A-Za-z0-9_-]+" autoCapitalize="characters" autoComplete="off" spellCheck={false} disabled={loading} className="w-full rounded-lg border border-aspen-blush-clay/50 bg-white px-3 py-3 text-aspen-deep-eucalyptus dark:bg-aspen-twilight-teal dark:text-aspen-pearl" aria-describedby="referral-help" />
              <p id="referral-help" className="mt-2 text-xs text-aspen-sage-teal dark:text-aspen-gentle-teal">New accounts start with {ASPEN_PRICING.freeLifetimeInbound} texts to Aspen and {ASPEN_PRICING.freeLifetimeOutbound} replies. Use a friend’s code and verify your phone before your first conversation to add {ASPEN_PRICING.referralBonusInbound} texts to Aspen and {ASPEN_PRICING.referralBonusOutbound} replies for each of you, valid for your current billing period. One referral credit per new account.</p>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !phoneNumber || retrySeconds > 0 || !challengeReady}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-aspen-sage-teal hover:bg-aspen-sage-teal/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-aspen-sage-teal disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </div>
              ) : (
                retrySeconds > 0 ? `Try again in ${retryLabel}` : !config ? 'Loading verification…' : 'Send Verification Code'
              )}
            </button>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleVerifyCode}>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-aspen-deep-eucalyptus dark:text-aspen-pearl mb-2">
                Verification Code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={handleCodeChange}
                placeholder="123456"
                maxLength={6}
                className="appearance-none relative block w-full px-3 py-3 border border-aspen-blush-clay/30 dark:border-aspen-moonlit-clay placeholder-aspen-sage-teal/50 text-aspen-deep-eucalyptus dark:text-aspen-pearl bg-white dark:bg-aspen-twilight-teal rounded-lg focus:outline-none focus:ring-2 focus:ring-aspen-sage-teal focus:border-aspen-sage-teal focus:z-10 sm:text-sm text-center text-2xl tracking-widest"
              />
              <p className="mt-1 text-xs text-aspen-sage-teal/70 dark:text-aspen-gentle-teal/70">
                Code sent to {formattedPhone}
                {timeRemaining > 0 && (
                  <span className="ml-2">
                    Expires in {minutesRemaining}:{secondsRemaining.toString().padStart(2, '0')}
                  </span>
                )}
              </p>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
              </div>
            )}

            <button type="button" onClick={handleSendCode}
              disabled={loading || retrySeconds > 0 || !challengeReady}
              className="w-full text-sm text-aspen-sage-teal dark:text-aspen-gentle-teal disabled:opacity-60 disabled:cursor-not-allowed">
              {retrySeconds > 0 ? `Resend available in ${retryLabel}` : 'Resend code'}
            </button>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                className="flex-1 py-3 px-4 border border-aspen-blush-clay/50 dark:border-aspen-moonlit-clay text-sm font-medium rounded-lg text-aspen-deep-eucalyptus dark:text-aspen-pearl bg-white dark:bg-aspen-twilight-teal hover:bg-aspen-morning-mist dark:hover:bg-aspen-moonlit-clay/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-aspen-sage-teal transition-colors duration-200"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-aspen-sage-teal hover:bg-aspen-sage-teal/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-aspen-sage-teal disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify & Login'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Development helper */}
        {process.env.NODE_ENV === 'development' && step === 'phone' && (
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-xs text-yellow-700 dark:text-yellow-400 font-medium">
              Development Mode: Use phone number 8675309 for testing
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
